// "Where is this group working, relative to its own grade?" — now, and how it
// got there.
//
// The one thing the year-so-far figure knows that the curriculum breakdown next
// door does not. By-curriculum counts standards and files them under a unit; a
// unit has a grade, but a LEARNER's distance from it is per learner, and
// averaging category labels does not produce it. So the server places each
// learner by the mean signed distance of their work from the grade they held in
// this window, and this reads out the counts.
//
// Counting LEARNERS, not standards. That distinction is the whole design
// problem here, because the timeline directly below is ALSO shaded by
// grade-relative placement in the same four textures — its bands and its
// entering strip are the same question asked of the WORK. Two grey textured
// things on one page counting different populations is exactly the confusion
// worth engineering against, so they are separated by geometry rather than by
// hoping the reader reads the captions:
//
//   varying height, absolute counts   -> standards (the timeline)
//   constant height, 100% stacked     -> learners  (this)
//
// A full-height band that never changes height cannot be read as a count of
// anything; it reads as a population splitting, which is what it is. The axis
// label says "% of learners" and the timeline's own strip says "new standards",
// so the words agree with the geometry.
//
// Per subject wherever the payload carries more than one, because the mixed
// mean is the number that can be wrong in both directions at once -- a learner
// a year behind in reading and a year ahead in maths averages to exactly at
// grade, which is true of nothing about them.
//
// ENCODING: texture on ink grey, the same four patterns the timeline shades its
// bands with, and for the same reason the district dashboard's chips spend a
// glyph rather than a hue (see tokens.LEVEL_REL) -- grade-relative placement has
// no colour anywhere in this app, and giving it one here would put a fourth
// categorical hue on a page that already carries three.
import React from "react";
import { useElementWidth } from "../../impact/ui";
import { MUTED, PROSE } from "../../impact/tokens";
import { GrowthDefs, REL, REL_ORDER, fillFor } from "./palette";
import { bucketLabel } from "./scale";

const SCOPE = "placement";
const BAND_H = 34;

const SUBJECT_LABEL = { MATH: "Math", READING: "Reading" };

// A column the cohort is too small to support. The series is cumulative, so the
// left end is genuinely four learners in August and a four-learner denominator
// swings twenty points on one child — drawn, because the run-in is real, but
// faded, because it is not evidence.
const THIN_FLOOR = 5;
const THIN_SHARE = 0.25;

// Painting order for the band, top of the column first. See the note at its
// use: the reverse of the legend's order is what puts "below" at the bottom.
const STACK = [...REL_ORDER].reverse();

// Which rows to draw. One per subject when the payload holds several, and the
// combined row alone when it holds one -- a "Both" row above a single "Math"
// row saying the same numbers is a row that tells the reader nothing.
const rowsOf = (placement) => {
  if (!placement) return [];
  const subjects = Object.keys(placement).filter((k) => k !== "all");
  if (subjects.length > 1)
    return subjects.sort().map((s) => [SUBJECT_LABEL[s] || s, s]);
  const only = subjects[0];
  return [[SUBJECT_LABEL[only] || null, "all"]];
};

// The share text under the band. `unknown` is named "unplaced" and kept in the
// sentence rather than quietly dropped: it is a data-quality residual and not a
// finding, but on a real district it runs to a tenth of the roster (no
// resolvable grade, or only sentinel-level courses), and three percentages that
// visibly fail to reach 100 read as a bug in the arithmetic rather than as a
// gap in the data.
const shares = (band) => {
  const n = band.learners || 0;
  const pct = (k) => (n ? Math.round((band[k] / n) * 100) : 0);
  return REL_ORDER.filter((r) => band[r]).map((r) =>
    r === "unknown" ? `${pct(r)}% unplaced` : `${pct(r)}% ${REL[r].short}`
  );
};

// One subject's year: a 100%-stacked column per bucket, in bucket order.
//
// Measured rather than drawn into a percentage viewBox that is then stretched to
// fit. The textures are SVG patterns in user space, so a viewBox scaled 3.5x
// horizontally and 1x vertically shears every 45-degree hatch to something
// close to horizontal — the encoding survives being small, but not being
// squashed, and "up" and "down" stop being distinguishable at all. Costs one
// ResizeObserver, which `useElementWidth` already owns.
const PlacementBand = ({ series, subject, interval, buckets }) => {
  const [ref, width] = useElementWidth();
  const cols = series.map((b) => (b.placement || {})[subject] || {});
  const last = cols.reduce((m, c) => Math.max(m, c.learners || 0), 0);
  const floor = Math.max(THIN_FLOOR, last * THIN_SHARE);
  const iw = Math.max(80, width || 0);
  const w = iw / Math.max(1, cols.length);
  return (
    <div ref={ref}>
    <svg
      width={iw}
      height={BAND_H}
      style={{ display: "block", borderRadius: 2 }}
      role="img"
      aria-label={`Share of learners working below, at and above grade level, ${
        cols.length
      } buckets`}
    >
      <GrowthDefs scope={SCOPE} />
      {cols.map((band, i) => {
        const n = band.learners || 0;
        if (!n) return null;
        let y = 0;
        return (
          <g key={i} opacity={n < floor ? 0.32 : 1}>
            {/* Drawn top-down in REVERSE number-line order, so on screen the
                bands read upward: below grade against the floor, on grade
                above it, above grade on top. A stack whose y axis disagrees
                with the words "below" and "above" makes the reader translate
                every glance, and the hatches — which rise for above and fall
                for below — then point the opposite way to the band they are
                in. The unplaced residual parks at the far end, past "above",
                rather than taking the baseline the number line wants. */}
            {STACK.map((rel) => {
              const h = ((band[rel] || 0) / n) * BAND_H;
              if (h <= 0) return null;
              const top = y;
              y += h;
              return (
                <rect
                  key={rel}
                  x={i * w}
                  y={top}
                  width={w}
                  height={h}
                  fill={fillFor(SCOPE, "neutral", rel, true)}
                />
              );
            })}
            <title>
              {`${buckets[i] ? bucketLabel(buckets[i], interval) : ""} — ${
                shares(band).join(", ") || "level unknown"
              } (${n} learner${n === 1 ? "" : "s"})`}
            </title>
          </g>
        );
      })}
    </svg>
    </div>
  );
};

// Sits under the rail's numbers as a full-width row. Two subjects each need a
// band and a sentence, and squeezed into a 190px rail column that is three
// stacked lines of 12px text against four one-line neighbours -- the rail grid
// then sizes every cell to the tallest and the card fills with white. Across
// the width the two subjects sit side by side and the textures are wide enough
// to count.
const GradePlacement = ({ placement, series = [], buckets = [], interval }) => {
  const rows = rowsOf(placement);
  const drawable = series.some((b) => b.placement);
  if (!rows.length) return null;
  const span =
    buckets.length > 1
      ? `${bucketLabel(buckets[0], interval)} → ${bucketLabel(
          buckets[buckets.length - 1],
          interval
        )}`
      : null;
  return (
    <div
      style={{
        display: "grid",
        gap: "10px 28px",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      }}
    >
      {rows.map(([label, key]) => {
        const band = placement[key] || {};
        if (!band.learners) return null;
        return (
          <div key={key}>
            <div
              style={{
                fontSize: 11,
                color: MUTED,
                marginBottom: 3,
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>{label || "% of learners"}</span>
              {drawable && span && <span>{span}</span>}
            </div>
            {drawable ? (
              <PlacementBand
                series={series}
                subject={key}
                interval={interval}
                buckets={buckets}
              />
            ) : null}
            <div
              style={{
                fontSize: 12,
                color: PROSE,
                marginTop: 3,
                lineHeight: 1.35,
              }}
            >
              {drawable ? "Now: " : ""}
              {shares(band).join(" · ") || "level unknown"}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Whether there is anything to say. Exported so the rail can drop the cell
// rather than render an empty one -- on a payload from a backend that predates
// `placement`, and on a cohort whose every standard is unlevelled.
const hasPlacement = (placement) =>
  rowsOf(placement).some(
    (r) => placement[r[1]] && placement[r[1]].learners > 0
  );

export { GradePlacement, hasPlacement };
